import {
  CalloutColor,
  ContentBlockType,
  SolverBlock,
  StudyNoteDocument,
  SuperLatexBlock,
  WidgetBlock,
} from '../../model';
import { DEFAULT_WIDGET_CONFIG } from '../extensions/widget/widget.model';

export const SAMPLE_CONTENT: StudyNoteDocument = {
  metadata: {
    title: 'Introduction to Algebra',
    slug: 'intro-to-algebra',
    status: 'draft',
  },
  content: [
    {
      id: 'md-intro-001',
      type: ContentBlockType.Markdown,
      markdown:
        '# Introduction to Algebra\n\nAlgebra is a {{popover:pop-1}}branch of mathematics{{/popover}} that uses **symbols** and **letters** to represent numbers and quantities in formulas and equations.',
      popovers: {
        'pop-1': {
          content: [
            {
              id: 'pop-1-content',
              type: ContentBlockType.Markdown,
              markdown:
                'Some **formatted** content\n\nAlgebra helps us solve equations and understand relationships between variables.',
            },
          ],
          linkUrl: '/playground',
        },
      },
    },
    {
      id: 'callout-key-point-001',
      type: ContentBlockType.Callout,
      title: 'Good to know',
      color: CalloutColor.Indigo,
      icon: 'puzzle-piece',
      content: [
        {
          id: 'md-callout-001',
          type: ContentBlockType.Markdown,
          markdown:
            'Algebra forms the foundation for all higher mathematics. Mastering these concepts will help you succeed in calculus, statistics, and beyond.',
        },
      ],
    },
    {
      id: 'md-intro-002',
      type: ContentBlockType.Markdown,
      markdown: "## Key Terms\n\nBefore diving into algebraic expressions, let's understand some fundamental concepts:",
    },
    {
      id: 'table-001',
      type: ContentBlockType.Table,
      has_borders: true,
      rows: [
        {
          cells: [
            {
              is_header: true,
              colwidth: [120],
              content: [{ id: 'md-001', type: ContentBlockType.Markdown, markdown: 'Concept' }],
            },
            {
              is_header: true,
              colwidth: [280],
              content: [{ id: 'md-002', type: ContentBlockType.Markdown, markdown: 'Description' }],
            },
            {
              is_header: true,
              colwidth: [150],
              text_align: 'center',
              content: [{ id: 'md-003', type: ContentBlockType.Markdown, markdown: 'Example' }],
            },
          ],
        },
        {
          cells: [
            {
              background_color: 'var(--tt-color-highlight-blue)',
              content: [{ id: 'md-004', type: ContentBlockType.Markdown, markdown: '_Variable_' }],
            },
            { content: [{ id: 'md-005', type: ContentBlockType.Markdown, markdown: 'A symbol representing a value' }] },
            { text_align: 'center', content: [{ id: 'md-006', type: ContentBlockType.Markdown, markdown: 'x, y, z' }] },
          ],
        },
        {
          cells: [
            {
              background_color: 'var(--tt-color-highlight-blue)',
              content: [{ id: 'md-007', type: ContentBlockType.Markdown, markdown: 'Constant' }],
            },
            { content: [{ id: 'md-008', type: ContentBlockType.Markdown, markdown: 'A fixed numerical value' }] },
            {
              text_align: 'center',
              content: [{ id: 'md-009', type: ContentBlockType.Markdown, markdown: '$\\pi$, $e$, 5' }],
            },
          ],
        },
        {
          cells: [
            {
              background_color: 'var(--tt-color-highlight-blue)',
              content: [{ id: 'md-010', type: ContentBlockType.Markdown, markdown: 'Coefficient' }],
            },
            { content: [{ id: 'md-011', type: ContentBlockType.Markdown, markdown: 'Number multiplying a variable' }] },
            {
              text_align: 'center',
              content: [{ id: 'md-012', type: ContentBlockType.Markdown, markdown: '$3$ in $3x$' }],
            },
          ],
        },
        {
          cells: [
            {
              background_color: 'var(--tt-color-highlight-green)',
              content: [{ id: 'md-013', type: ContentBlockType.Markdown, markdown: 'Quadratic' }],
            },
            {
              content: [
                { id: 'md-014', type: ContentBlockType.Markdown, markdown: 'Equation with $x^2$ term' },
                {
                  id: 'callout-in-cell',
                  type: ContentBlockType.Callout,
                  title: 'Did you know?',
                  color: CalloutColor.Teal,
                  content: [
                    {
                      id: 'md-callout-in-cell',
                      type: ContentBlockType.Markdown,
                      markdown: 'The quadratic formula can solve **any** quadratic equation!',
                    },
                  ],
                },
                {
                  id: 'toggle-in-cell',
                  type: ContentBlockType.Toggle,
                  title: 'See the formula',
                  content: [
                    {
                      id: 'md-toggle-in-cell',
                      type: ContentBlockType.Markdown,
                      markdown: '$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$',
                    },
                  ],
                },
              ],
            },
            {
              text_align: 'center',
              content: [{ id: 'md-015', type: ContentBlockType.Markdown, markdown: '$ax^2 + bx + c = 0$' }],
            },
          ],
        },
      ],
    },
    {
      id: 'md-examples-001',
      type: ContentBlockType.Markdown,
      markdown:
        '## Working with Expressions\n\nAn *algebraic expression* combines variables, constants, and operations. Here are some examples:\n\n- $2x + 3$ — a {{popover:pop-linear}}linear expression{{/popover}}\n- $x^2 + 2x + 1$ — a {{popover:pop-quadratic}}quadratic expression{{/popover}}\n- $3xy - 5$ — a multi-variable expression',
      popovers: {
        'pop-linear': {
          content: [
            {
              id: 'pop-linear-content',
              type: ContentBlockType.Markdown,
              markdown:
                'A **linear expression** has a maximum degree of 1. The graph of a linear equation is always a straight line.',
            },
          ],
          linkUrl: 'https://en.wikipedia.org/wiki/Linear_equation',
        },
        'pop-quadratic': {
          content: [
            {
              id: 'pop-quadratic-content',
              type: ContentBlockType.Markdown,
              markdown:
                'A **quadratic expression** has a degree of 2. Its graph forms a parabola that opens upward or downward.',
            },
          ],
        },
      },
    },
    {
      id: 'md-code-001',
      type: ContentBlockType.Markdown,
      markdown: 'In programming, you might write: `const result = 2 * x + 3;`',
    },
    {
      id: 'callout-tip-001',
      type: ContentBlockType.Callout,
      title: 'Pro Tip',
      color: CalloutColor.Teal,
      icon: 'lightbulb',
      content: [
        {
          id: 'md-callout-tip-001',
          type: ContentBlockType.Markdown,
          markdown:
            'When simplifying expressions, always combine *like terms* first. Like terms have the same variable raised to the same power.',
        },
      ],
    },
    {
      id: 'md-latex-001',
      type: ContentBlockType.Markdown,
      markdown:
        "## Mathematical Formulas\n\nHere are some common formulas using LaTeX:\n\nThe quadratic formula: $x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$\n\nEuler's identity: $e^{i\\pi} + 1 = 0$",
    },
    {
      id: 'md-latex-002',
      type: ContentBlockType.Markdown,
      markdown:
        'A block equation for the sum of a geometric series:\n\n$$S_n = a \\cdot \\frac{1 - r^n}{1 - r}$$\n\nAnd the Pythagorean theorem: $$a^2 + b^2 = c^2$$',
    },
    {
      id: 'callout-warning-001',
      type: ContentBlockType.Callout,
      title: 'Common Mistake',
      color: CalloutColor.Red,
      icon: 'warning',
      content: [
        {
          id: 'md-callout-warning-001',
          type: ContentBlockType.Markdown,
          markdown:
            'Remember that $\\sqrt{a^2 + b^2} \\neq a + b$. The square root of a sum is **not** equal to the sum of the square roots!\n\nFor example: $\\sqrt{9 + 16} = \\sqrt{25} = 5$, but $\\sqrt{9} + \\sqrt{16} = 3 + 4 = 7$.',
        },
      ],
    },
    {
      id: 'toggle-example-001',
      type: ContentBlockType.Toggle,
      title: 'Worked Example: Solving a Quadratic Equation',
      content: [
        {
          id: 'md-toggle-example-001',
          type: ContentBlockType.Markdown,
          markdown:
            '**Problem:** Solve $x^2 - 5x + 6 = 0$\n\n**Solution:**\n\n1. Factor the quadratic: $(x - 2)(x - 3) = 0$\n2. Set each factor to zero:\n   - $x - 2 = 0 \\Rightarrow x = 2$\n   - $x - 3 = 0 \\Rightarrow x = 3$\n\n**Answer:** $x = 2$ or $x = 3$',
        },
      ],
    },
    {
      id: 'toggle-derivation-001',
      type: ContentBlockType.Toggle,
      title: 'Derivation of the Quadratic Formula',
      content: [
        {
          id: 'md-toggle-derivation-001',
          type: ContentBlockType.Markdown,
          markdown:
            'Starting with $ax^2 + bx + c = 0$, we complete the square:\n\n1. Divide by $a$: $x^2 + \\frac{b}{a}x + \\frac{c}{a} = 0$\n2. Move constant: $x^2 + \\frac{b}{a}x = -\\frac{c}{a}$\n3. Complete the square: $\\left(x + \\frac{b}{2a}\\right)^2 = \\frac{b^2 - 4ac}{4a^2}$\n4. Take square root and solve for $x$:\n\n$$x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}$$',
        },
      ],
    },
    {
      id: 'md-superlatex-intro',
      type: ContentBlockType.Markdown,
      markdown: '## Super LaTeX Examples\n\nBelow are some examples of Super LaTeX blocks with advanced features:',
    },
    {
      id: 'superlatex-001',
      type: ContentBlockType.SuperLatex,
      custom_css: 'bg-amber-50 border border-amber-200 rounded-lg p-4',
      latex_nodes: [
        {
          latex: `$\\htmlData{anchor=left-base}{(a + b)}^{\\htmlData{anchor=left-exp}{n}} = \\sum_{k=0}^{n} \\binom{n}{k} \\htmlData{anchor=term-ank}{a^{n-k}} b^k$`,
        },
      ],
      annotations: [
        {
          id: 'ank-annotation',
          target_anchor: 'term-ank',
          content: '\\text{Term with exponent } n-k',
          trigger: 'click',
          placement: 'top',
        },
      ],
    } satisfies SuperLatexBlock,
    {
      id: 'superlatex-002',
      type: ContentBlockType.SuperLatex,
      latex_nodes: [
        {
          latex: `$\\htmlData{anchor=example-base}{(\\htmlData{anchor=example-2x}{2x} + \\htmlData{anchor=example-3}{3})}^{\\htmlData{anchor=example-exp}{3}} \\quad | \\quad \\htmlData{anchor=general-base}{(\\htmlData{anchor=general-a}{a} + \\htmlData{anchor=general-b}{b})}^{\\htmlData{anchor=general-n}{n}}$`,
        },
      ],
      annotations: [
        {
          id: 'example-annotation',
          target_anchor: 'example-2x',
          content: 'Highlighted coefficient',
          trigger: 'hover',
          placement: 'top',
        },
      ],
      connections: [
        {
          id: '2x-to-a',
          from_anchor: 'example-2x',
          to_anchor: 'general-a',
          style: {
            color: 'sky',
            width: 2,
            line_style: 'dashed',
            label: {
              text: '$a$ **coefficient**',
              style: 'square-box',
              offset: { x: -10, y: 10 },
            },
            from_socket: 'Bottom',
            to_socket: 'Bottom',
            stub: 15,
          },
        },
        {
          id: 'exp-to-n',
          from_anchor: 'example-exp',
          to_anchor: 'general-n',
          style: {
            color: 'red',
            width: 2,
            line_style: 'dashed',
            label: {
              text: '$n$ _exponent_',
              style: 'square-box',
            },
            from_socket: 'Top',
            to_socket: 'Top',
            stub: 15,
          },
        },
      ],
      highlighted_anchors: {
        'example-2x': { style: 'text-color-with-background', color: 'sky' },
      },
    } satisfies SuperLatexBlock,
    {
      id: 'superlatex-003',
      type: ContentBlockType.SuperLatex,
      latex_nodes: [
        {
          latex: `$\\frac{d}{dx}(\\htmlData{anchor=derivative-x3}{x^3} + {2x^2} - 5x + 1) = \\htmlData{anchor=result-3x2}{3x^2} + {4x} - \\htmlData{anchor=result-minus5}{5}$`,
        },
        {
          latex: `$\\htmlData{anchor=fprime}{f'(x)} = 3x^2 + 4x - 5$`,
        },
        {
          latex: `$\\htmlData{anchor=yprime}{y'} = 3x^2 + 4x - 5$`,
        },
      ],
      connections: [
        {
          id: 'x3-to-3x2',
          from_anchor: 'derivative-x3',
          to_anchor: 'result-3x2',
          style: {
            color: 'sky',
            width: 2,
            line_style: 'dashed',
            from_socket: 'Top',
            to_socket: 'Top',
            stub: 15,
          },
        },
        {
          id: 'fprime-to-minus5',
          from_anchor: 'fprime',
          to_anchor: 'result-minus5',
          style: {
            color: 'purple',
            width: 2,
            line_style: 'solid',
            from_socket: 'Top',
            to_socket: 'Bottom',
            stub: 20,
          },
        },
        {
          id: 'fprime-to-yprime',
          from_anchor: 'fprime',
          to_anchor: 'yprime',
          style: {
            color: 'green',
            width: 2,
            line_style: 'dashed',
            label: {
              text: "$f'(x) \\equiv y'$",
              style: 'square-box',
              location: 0.4,
              offset: { x: -10, y: 10 },
            },
            from_socket: 'Left',
            to_socket: 'Left',
            stub: 30,
          },
        },
      ],
    } satisfies SuperLatexBlock,
    {
      id: 'md-solver-intro',
      type: ContentBlockType.Markdown,
      markdown:
        '## Interactive Solvers\n\nBelow are interactive step-by-step solvers that demonstrate mathematical concepts:',
    },
    {
      id: 'solver-derivative-example',
      type: ContentBlockType.Solver,
      name: 'Derivative Walkthrough',
      content: [
        {
          id: 'derivative-stem-1',
          type: ContentBlockType.Markdown,
          markdown: 'Find the derivative of the function $f(x) = x^3 + 2x^2 - 5x + 1$',
        },
      ],
      slides: [
        {
          name: 'Just a demo slide',
          initial_super_latex_content: {
            latex_nodes: [
              {
                id: 'line-1',
                latex: `$\\frac{d}{dx}(\\htmlData{anchor=derivative-x3}{x^3} + {2x^2} - 5x + 1) = \\htmlData{anchor=result-3x2}{3x^2} + {4x} - \\htmlData{anchor=result-minus5}{5}$`,
              },
              {
                id: 'line-2',
                latex: `$\\htmlData{anchor=fprime}{f'(x)} = 3x^2 + 4x - 5$`,
              },
              {
                id: 'line-3',
                latex: `$\\htmlData{anchor=yprime}{y'} = 3x^2 + 4x - 5$`,
              },
            ],
          },
          initial_description_content: [
            {
              type: ContentBlockType.Markdown,
              id: 'desc-1',
              markdown: 'Step 1: Just the equations (no connections or annotations)',
            },
          ],
          steps: [
            {
              title: 'Just the equations (no connections or annotations)',
            },
            {
              title: 'Connections appear between related terms',
              super_latex_actions: [
                {
                  type: 'add_connections',
                  connections: [
                    {
                      id: 'x3-to-3x2',
                      from_anchor: 'derivative-x3',
                      to_anchor: 'result-3x2',
                      style: {
                        color: 'sky',
                        width: 2,
                        line_style: 'dashed',
                        from_socket: 'Top',
                        to_socket: 'Top',
                        stub: 15,
                      },
                    },
                    {
                      id: 'fprime-to-minus5',
                      from_anchor: 'fprime',
                      to_anchor: 'result-minus5',
                      style: {
                        color: 'purple',
                        width: 2,
                        line_style: 'solid',
                        from_socket: 'Top',
                        to_socket: 'Bottom',
                        stub: 20,
                      },
                    },
                  ],
                },
              ],
              description_content_actions: [
                {
                  type: 'replace_item',
                  item_id: 'desc-1',
                  new_item: {
                    type: ContentBlockType.Markdown,
                    id: 'desc-1',
                    markdown: 'Step 2: Connections appear between related terms',
                  },
                },
              ],
            },
            {
              title: "Annotation added (click $f'(x)$ to see it) + $y'$ highlighted in green",
              super_latex_actions: [
                {
                  type: 'add_annotations',
                  annotations: [
                    {
                      id: 'fprime-annotation',
                      target_anchor: 'fprime',
                      content: "This is **Lagrange's notation** for derivatives",
                      trigger: 'click',
                      placement: 'top',
                    },
                  ],
                },
                {
                  type: 'add_highlights',
                  highlights: {
                    yprime: { style: 'text-color-with-background', color: 'green' },
                  },
                },
              ],
              description_content_actions: [
                {
                  type: 'replace_item',
                  item_id: 'desc-1',
                  new_item: {
                    type: ContentBlockType.Markdown,
                    id: 'desc-1',
                    markdown: "Step 3: Annotation added (click $f'(x)$ to see it) + $y'$ highlighted in green",
                  },
                },
              ],
            },
            {
              title: 'New equation inserted (Leibniz notation $\\frac{dy}{dx}$)',
              super_latex_actions: [
                {
                  type: 'insert_nodes',
                  after_id: 'line-2',
                  nodes: [
                    {
                      id: 'line-2-5',
                      latex: `$\\frac{dy}{dx} = 3x^2 + 4x - 5$`,
                    },
                  ],
                },
              ],
              description_content_actions: [
                {
                  type: 'replace_item',
                  item_id: 'desc-1',
                  new_item: {
                    type: ContentBlockType.Markdown,
                    id: 'desc-1',
                    markdown: 'Step 4: New equation inserted (Leibniz notation $\\frac{dy}{dx}$)',
                  },
                },
                {
                  type: 'add_items',
                  position: 'end',
                  items: [
                    {
                      type: ContentBlockType.Markdown,
                      id: 'desc-new-line',
                      markdown: 'This is a **new line of text** that should animate in.',
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    } satisfies SolverBlock,
    {
      id: 'solver-quadratic-example',
      type: ContentBlockType.Solver,
      name: 'EXAMPLE 1.3.2',
      content: [
        {
          id: 'stem-1',
          type: ContentBlockType.Markdown,
          markdown: 'Let $f(x) = x^3$. Find the equation of the normal at $x = 2$.',
        },
      ],
      slides: [
        {
          name: 'Determine the number of possible solutions',
          initial_super_latex_content: {
            latex_nodes: [
              { id: 'step1-page1-line1', latex: '$\\Delta = b^2 - 4ac$' },
              {
                id: 'step1-page1-line2',
                latex: '$= (-8)^2 - 4 \\cdot 3 \\cdot 4 = 16 > 0$',
              },
            ],
          },
          initial_description_content: [
            {
              type: ContentBlockType.Markdown,
              id: 'desc-q1',
              markdown: 'The quadratic function has two distinct **real** roots',
            },
          ],
        },
        {
          name: 'Use the quadratic formula to solve for the solutions',
          initial_super_latex_content: {
            latex_nodes: [
              {
                id: 'step2-page1-line1',
                latex: '$x_{1,2} = \\frac{-b \\pm \\sqrt{\\Delta}}{2a}$',
              },
              {
                id: 'step2-page1-line2',
                latex: '$x_{1,2} = \\frac{8 \\pm \\sqrt{16}}{2 \\cdot 3}$',
              },
              {
                id: 'step2-page1-line3',
                latex: '$x_{1} = 2, \\quad x_{2} = \\frac{2}{3}$',
              },
            ],
          },
          initial_description_content: [
            {
              type: ContentBlockType.Markdown,
              id: 'desc-q2',
              markdown: 'The quadratic function has two distinct **real** roots',
            },
          ],
        },
      ],
    } satisfies SolverBlock,
    {
      id: 'md-widget-intro',
      type: ContentBlockType.Markdown,
      markdown:
        '## Interactive Widgets\n\nWidgets allow embedding interactive visuals directly in study notes. The block below is a quadratic function widget.',
    },
    {
      id: 'widget-quadratic-001',
      type: ContentBlockType.Widget,
      widget_type: 'quadratic-function',
      title: 'Quadratic Function Explorer',
      custom_css: 'mt-4',
      custom_style: { 'border-radius': '12px' },
      config: {
        ...DEFAULT_WIDGET_CONFIG,
        id: 'widget-quadratic-001',
        params: { a: 1, b: -2, c: -3 },
        display: {
          ...DEFAULT_WIDGET_CONFIG.display,
          grid: true,
          axes: true,
        },
      },
    } satisfies WidgetBlock,
    {
      id: 'md-edge-cases-intro',
      type: ContentBlockType.Markdown,
      custom_css: 'mt-8',
      custom_style: { 'border-left': '4px solid var(--tt-color-highlight-blue)', padding: '8px 12px' },
      markdown: '## Edge Cases\n\nThe blocks below cover empty states, custom styles, and nested structures.',
    },
    {
      id: 'md-empty-001',
      type: ContentBlockType.Markdown,
      markdown: '',
    },
    {
      id: 'toggle-empty-001',
      type: ContentBlockType.Toggle,
      title: 'Empty toggle content',
      content: [],
    },
    {
      id: 'callout-empty-001',
      type: ContentBlockType.Callout,
      title: 'Empty callout content',
      color: CalloutColor.Orange,
      icon: 'circle-question',
      content: [],
    },
    {
      id: 'callout-nested-001',
      type: ContentBlockType.Callout,
      title: 'Nested structures',
      color: CalloutColor.Green,
      icon: 'layer-group',
      custom_css: 'bg-neutral-0 border border-neutral-200',
      content: [
        {
          id: 'toggle-nested-001',
          type: ContentBlockType.Toggle,
          title: 'Toggle inside callout',
          content: [
            {
              id: 'md-nested-001',
              type: ContentBlockType.Markdown,
              markdown: 'Markdown inside a toggle nested within a callout.',
            },
            {
              id: 'table-nested-001',
              type: ContentBlockType.Table,
              has_borders: false,
              rows: [
                {
                  cells: [
                    {
                      is_header: true,
                      content: [{ id: 'md-nested-002', type: ContentBlockType.Markdown, markdown: 'Key' }],
                    },
                    {
                      is_header: true,
                      content: [{ id: 'md-nested-003', type: ContentBlockType.Markdown, markdown: 'Value' }],
                    },
                  ],
                },
                {
                  cells: [
                    { content: [{ id: 'md-nested-004', type: ContentBlockType.Markdown, markdown: 'A' }] },
                    { content: [{ id: 'md-nested-005', type: ContentBlockType.Markdown, markdown: 'B' }] },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'table-edge-001',
      type: ContentBlockType.Table,
      has_borders: false,
      custom_style: { width: '80%', margin: '0 auto' },
      rows: [
        {
          cells: [
            {
              is_header: true,
              text_align: 'center',
              content: [{ id: 'md-edge-001', type: ContentBlockType.Markdown, markdown: 'Empty cell next' }],
            },
            {
              is_header: true,
              text_align: 'center',
              content: [{ id: 'md-edge-002', type: ContentBlockType.Markdown, markdown: 'Content cell' }],
            },
          ],
        },
        {
          cells: [
            { content: [] },
            { content: [{ id: 'md-edge-003', type: ContentBlockType.Markdown, markdown: 'Cell content' }] },
          ],
        },
      ],
    },
    {
      id: 'superlatex-empty-001',
      type: ContentBlockType.SuperLatex,
      custom_css: 'border border-dashed border-neutral-200 rounded-lg p-3',
      latex_nodes: [],
    } satisfies SuperLatexBlock,
    {
      id: 'md-deep-nesting-intro',
      type: ContentBlockType.Markdown,
      markdown: '## Deep Nesting Scenarios\n\nThese blocks intentionally nest multiple levels deep.',
    },
    {
      id: 'callout-deep-001',
      type: ContentBlockType.Callout,
      title: 'Level 1 callout',
      color: CalloutColor.Yellow,
      icon: 'sitemap',
      content: [
        {
          id: 'toggle-deep-001',
          type: ContentBlockType.Toggle,
          title: 'Level 2 toggle',
          content: [
            {
              id: 'callout-deep-002',
              type: ContentBlockType.Callout,
              title: 'Level 3 callout',
              color: CalloutColor.Teal,
              content: [
                {
                  id: 'toggle-deep-002',
                  type: ContentBlockType.Toggle,
                  title: 'Level 4 toggle',
                  content: [
                    {
                      id: 'md-deep-001',
                      type: ContentBlockType.Markdown,
                      markdown: 'Level 5 markdown leaf node.',
                    },
                    {
                      id: 'superlatex-deep-001',
                      type: ContentBlockType.SuperLatex,
                      custom_css: 'border border-neutral-200 rounded-lg p-3',
                      latex_nodes: [
                        {
                          latex:
                            '$\\htmlData{anchor=deep-x}{x}^2 + \\htmlData{anchor=deep-y}{y}^2 = \\htmlData{anchor=deep-z}{z}^2$',
                        },
                      ],
                      highlighted_anchors: {
                        'deep-z': { style: 'text-color-with-background', color: 'sky' },
                      },
                    } satisfies SuperLatexBlock,
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'table-deep-001',
      type: ContentBlockType.Table,
      has_borders: true,
      rows: [
        {
          cells: [
            {
              is_header: true,
              content: [{ id: 'md-deep-002', type: ContentBlockType.Markdown, markdown: 'Nested A' }],
            },
            {
              is_header: true,
              content: [{ id: 'md-deep-003', type: ContentBlockType.Markdown, markdown: 'Nested B' }],
            },
          ],
        },
        {
          cells: [
            {
              content: [
                {
                  id: 'toggle-deep-003',
                  type: ContentBlockType.Toggle,
                  title: 'Toggle in table cell',
                  content: [
                    {
                      id: 'callout-deep-003',
                      type: ContentBlockType.Callout,
                      title: 'Callout in toggle in table cell',
                      color: CalloutColor.Indigo,
                      content: [
                        {
                          id: 'md-deep-004',
                          type: ContentBlockType.Markdown,
                          markdown: 'Leaf inside table -> toggle -> callout.',
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            {
              content: [
                {
                  id: 'callout-deep-004',
                  type: ContentBlockType.Callout,
                  title: 'Callout in table cell',
                  color: CalloutColor.Red,
                  content: [
                    {
                      id: 'toggle-deep-004',
                      type: ContentBlockType.Toggle,
                      title: 'Toggle inside callout in table',
                      content: [
                        {
                          id: 'md-deep-005',
                          type: ContentBlockType.Markdown,
                          markdown: 'Another deep leaf node.',
                        },
                        {
                          id: 'widget-deep-001',
                          type: ContentBlockType.Widget,
                          widget_type: 'quadratic-function',
                          title: 'Widget inside nested table',
                          custom_css: 'mt-2',
                          config: {
                            ...DEFAULT_WIDGET_CONFIG,
                            id: 'widget-deep-001',
                            params: { a: -1, b: 0, c: 2 },
                          },
                        } satisfies WidgetBlock,
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'callout-mixed-001',
      type: ContentBlockType.Callout,
      title: 'Mixed nested blocks',
      color: CalloutColor.Pink,
      content: [
        {
          id: 'md-mixed-001',
          type: ContentBlockType.Markdown,
          markdown: 'This callout mixes multiple nested blocks below.',
        },
        {
          id: 'toggle-mixed-001',
          type: ContentBlockType.Toggle,
          title: 'Toggle with nested table + callout',
          content: [
            {
              id: 'solver-deep-001',
              type: ContentBlockType.Solver,
              name: 'Nested solver',
              content: [
                {
                  id: 'solver-deep-stem-001',
                  type: ContentBlockType.Markdown,
                  markdown: 'Solve $x^2 = 9$.',
                },
              ],
              slides: [
                {
                  name: 'Quick solution',
                  initial_super_latex_content: {
                    latex_nodes: [{ id: 'solver-deep-line-1', latex: '$x = \\pm 3$' }],
                  },
                  initial_description_content: [
                    {
                      id: 'solver-deep-desc-1',
                      type: ContentBlockType.Markdown,
                      markdown: 'Both $x=3$ and $x=-3$ satisfy the equation.',
                    },
                  ],
                },
              ],
            } satisfies SolverBlock,
            {
              id: 'table-mixed-001',
              type: ContentBlockType.Table,
              has_borders: false,
              rows: [
                {
                  cells: [
                    {
                      content: [
                        {
                          id: 'callout-mixed-002',
                          type: ContentBlockType.Callout,
                          title: 'Callout inside table',
                          color: CalloutColor.Sky,
                          content: [
                            {
                              id: 'toggle-mixed-002',
                              type: ContentBlockType.Toggle,
                              title: 'Toggle inside callout inside table',
                              content: [
                                {
                                  id: 'md-mixed-002',
                                  type: ContentBlockType.Markdown,
                                  markdown: 'Deep mixed nesting leaf.',
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};
